import { Chart } from "primereact/chart";
import { getFontFamily } from "../services/util.service";
import { useTheme } from "../context/ThemeContext";
import { useEffect, useMemo, useState } from "react";

export default function LineChart(
  { id, data, xText, yText, xOffset, yOffset, isScroll, className, onRangeSelect }: { id: string, data?: any, xText?: string, yText?: string, xOffset?: boolean, yOffset?: boolean, isScroll?: boolean, className?: string, onRangeSelect?: (start: number, end: number) => void }
) {

  const { theme } = useTheme()
  const textColor = theme === 'light' ? '#374151' : '#f3f4f6';
  const gridColor = theme === 'light' ? '#0000001A' : '#374151';
  const [fontSize, setFontSize] = useState(
    Number(localStorage.getItem("appFontSize")) || 14
  );

  useEffect(() => {
    const handleFontSizeChange = () => {
      setFontSize(Number(localStorage.getItem("appFontSize")) || 14);
    };

    window.addEventListener("fontSizeChanged", handleFontSizeChange);

    return () => {
      window.removeEventListener("fontSizeChanged", handleFontSizeChange);
    };
  }, []);

  const [fontFamily, setFontFamily] = useState(getFontFamily());

  useEffect(() => {
    const handleFontFamilyChange = () => {
      setFontFamily(getFontFamily());
    };

    window.addEventListener("fontFamilyChanged", handleFontFamilyChange);

    return () => {
      window.removeEventListener("fontFamilyChanged", handleFontFamilyChange);
    };
  }, []);

  const options = useMemo(
    () => ({
      plugins: {
        legend: {
          display: id === 'radio-rf-issues-over-time' ? false : true,
          position: "bottom",
          ...(["mode-degradation-trip", "undesirable-eb-trip", "unified-kpi-per-project"].includes(id) ? { } : { onClick: () => { }} ),
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
            padding: 12,
            color: textColor,
            font: {
              size: fontSize,
              family: fontFamily
            },
          }
        },
        ...(["rf-over-time"].includes(id) && {
          tooltip: {
            titleFont: {
              size: fontSize,
              family: fontFamily
            },
            bodyFont: {
              size: fontSize,
              family: fontFamily
            },
            footerFont: {
              size: fontSize,
              family: fontFamily
            },
            callbacks: {
              // label: function (context: any) {
              //   const value = context.parsed.y;
              //   const extra = context.dataset.extraInfo?.[context.dataIndex];

              //   return [
              //     `Temp: ${value}°C`,
              //     `Humidity: ${extra}%`
              //   ];
              // }
              afterLabel: (ctx: any) => {
                const index = ctx.dataIndex;
                const ds = ctx.dataset;
                return [
                  ...(ds?.event?.[index] ? [`Event: ${ds.event[index]}`] : []),
                  `Abs Location: ${ds.absLocation[index]}`,
                  `TCAS Mode: ${ds.tcasMode[index] ?? "NA"}`
                ];
              }
            }
          }
        }),
        ...(["speed-vs-time"].includes(id) && {
          tooltip: {
            titleFont: {
              size: fontSize,
              family: fontFamily
            },
            bodyFont: {
              size: fontSize,
              family: fontFamily
            },
            footerFont: {
              size: fontSize,
              family: fontFamily
            },
            callbacks: {
              label: (ctx: any) => {
                const value = ctx.parsed.y;

                return `${ctx.dataset.label}: ${value ?? "NA"} Kmph`;
              },

              afterLabel: (ctx: any) => {
                const index = ctx.dataIndex;
                const ds = ctx.dataset;

                return ds?.event?.[index]
                  ? [`Event: ${ds.event[index]}`]
                  : [];
              }
            }
          }
        }),
        ...(["trip-rf-over-time", "trip-speed-over-time"].includes(id) && {
          tooltip: {
            titleFont: {
              size: fontSize,
              family: fontFamily
            },
            bodyFont: {
              size: fontSize,
              family: fontFamily
            },
            footerFont: {
              size: fontSize,
              family: fontFamily
            },
            callbacks: {
              title: (context: any) => {
                const index = context[0].dataIndex;
                const chart = context[0].chart;

                return chart.data.timestamps?.[index - 1] || context[0].label;
              },

              label: (context: any) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y ?? 0;

                return `${label}: ${value}`;
              },

              afterLabel: (ctx: any) => {
                const index = ctx.dataIndex;
                const ds = ctx.dataset;

                const modes = (ds.tcasMode?.[index] || []).map((m: string) =>
                  m.replaceAll("_", " ")
                );

                return [
                  ...(ds?.event?.[index] ? [`Event: ${ds.event[index]}`] : []),

                  ...(modes.length
                    ? [
                      `TCAS Mode: ${modes[0]}`,
                      ...modes.slice(1).map((m: any) => `                      ${m}`) // spacing for alignment
                    ]
                    : ["TCAS Mode: NA"])
                ];
              }
            }
          }
        }),
        ...(["radio-rf-issues-over-time", "signal-aspect-blanking"].includes(id) && {
          tooltip: {
            titleFont: {
              size: fontSize,
              family: fontFamily
            },
            bodyFont: {
              size: fontSize,
              family: fontFamily
            },
            footerFont: {
              size: fontSize,
              family: fontFamily
            },
            callbacks: {
              // 1. X-axis label (top)
              title: function (context: any) {
                return context[0].label; // timeBucket
              },

              // 2. Date range (next line)
              afterTitle: (context: any) => {
                return context[0]?.chart?.data?.dateRange?.[context[0].dataIndex] || '';
              },

              // 3. Count (per dataset line)
              label: function (context: any) {
                const label = context.dataset.label;
                const value = context.parsed.y ?? 0;

                return `${label}: ${value}`;
              }
            }
          }
        }),
        ...(["events-over-time"].includes(id) && {
          tooltip: {
            mode: 'nearest',
            intersect: true,

            titleFont: {
              size: fontSize,
              family: fontFamily
            },

            bodyFont: {
              size: fontSize,
              family: fontFamily
            },

            footerFont: {
              size: fontSize,
              family: fontFamily
            },

            filter: (tooltipItem: any) => {
              const chart = tooltipItem.chart;
              const dataIndex = tooltipItem.dataIndex;

              // Check all datasets at this X-axis position
              const hasNonZero = chart.data.datasets.some((dataset: any) => {
                const value = Number(dataset.data?.[dataIndex] ?? 0);
                return value !== 0;
              });

              // If ALL events are zero,
              // allow the currently hovered point
              // so we can show "No Loco Event found"
              if (!hasNonZero) {
                return true;
              }

              // If at least one event has a value,
              // show tooltip ONLY for the hovered non-zero point
              const value = Number(tooltipItem.parsed?.y ?? 0);

              return value !== 0;
            },

            callbacks: {
              title: (context: any) => {
                if (!context?.length) {
                  return '';
                }

                return context[0]?.label ?? '';
              },

              afterTitle: (context: any) => {
                if (!context?.length) {
                  return '';
                }

                const chart = context[0]?.chart;
                const dataIndex = context[0]?.dataIndex;

                return chart?.data?.dateRange?.[dataIndex] ?? '';
              },

              beforeBody: (context: any) => {
                if (!context?.length) {
                  return '';
                }

                const chart = context[0].chart;
                const dataIndex = context[0].dataIndex;

                const hasNonZero = chart.data.datasets.some((dataset: any) => {
                  const value = Number(dataset.data?.[dataIndex] ?? 0);
                  return value !== 0;
                });

                return hasNonZero ? '' : 'No Loco Event found';
              },

              label: (context: any) => {
                const chart = context.chart;
                const dataIndex = context.dataIndex;

                const hasNonZero = chart.data.datasets.some((dataset: any) => {
                  const value = Number(dataset.data?.[dataIndex] ?? 0);
                  return value !== 0;
                });

                // All events are zero
                if (!hasNonZero) {
                  return '';
                }

                const value = Number(context.parsed?.y ?? 0);

                return `${context.dataset.label}: ${value}`;
              }
            }
          }
        }),
        ...(["mode-degradation-trip", "undesirable-eb-trip", "unified-kpi-per-project"].includes(id) && {
          tooltip: {
            mode: 'nearest',
            intersect: true,

            titleFont: {
              size: fontSize,
              family: fontFamily
            },

            bodyFont: {
              size: fontSize,
              family: fontFamily
            },

            footerFont: {
              size: fontSize,
              family: fontFamily
            },

            filter: (tooltipItem: any) => {
              const chart = tooltipItem.chart;
              const dataIndex = tooltipItem.dataIndex;

              // Check all datasets at this X-axis position
              const hasNonZero = chart.data.datasets.some((dataset: any) => {
                const value = Number(dataset.data?.[dataIndex] ?? 0);
                return value !== 0;
              });

              // If ALL events are zero,
              // allow the currently hovered point
              // so we can show "No Loco Event found"
              if (!hasNonZero) {
                return false;
              }

              // If at least one event has a value,
              // show tooltip ONLY for the hovered non-zero point
              const value = Number(tooltipItem.parsed?.y ?? 0);

              return value !== 0;
            },

            callbacks: {
              title: (context: any) => {
                if (!context?.length) {
                  return '';
                }

                return context[0]?.label ?? '';
              },

              afterTitle: (context: any) => {
                if (!context?.length) {
                  return '';
                }

                const chart = context[0]?.chart;
                const dataIndex = context[0]?.dataIndex;

                return chart?.data?.dateRange?.[dataIndex] ?? '';
              },

              label: (context: any) => {
                const chart = context.chart;
                const dataIndex = context.dataIndex;

                const hasNonZero = chart.data.datasets.some((dataset: any) => {
                  const value = Number(dataset.data?.[dataIndex] ?? 0);
                  return value !== 0;
                });

                // All events are zero
                if (!hasNonZero) {
                  return '';
                }

                const value = Number(context.parsed?.y ?? 0);

                return `${context.dataset.label}: ${value}`;
              },

              afterLabel: (ctx: any) => {
                const hasNonZero = ctx.chart.data.datasets.some((dataset: any) => {
                  const value = Number(dataset.data?.[ctx.dataIndex] ?? 0);
                  return value !== 0;
                });

                // All events are zero
                if (!hasNonZero) {
                  return '';
                }

                const project = ctx.dataset?.projects?.[ctx.dataIndex];

                if (!project) {
                  return '';
                }

                return `${project.project}: ${project.value}`;
              },
            }
          }
        }),
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ...(xOffset && { offset: true }),
          ticks: {
            color: textColor,
            font: {
              size: fontSize,
              family: fontFamily
            }
          },
          border: {
            display: true,
            color: gridColor
          },
          grid: {
            color: gridColor
          },
          ...(xText && {
            title: {
              display: true,
              color: textColor,
              text: xText,
              font: {
                size: fontSize,
                family: fontFamily
              }
            }
          })
        },
        y: {
          ...(yOffset && { offset: true }),
          ...(['trip-speed-over-time', "speed-vs-time"].includes(id) && {
            // ...(data?.isAllZero && { min: 0 }),
            ...(data?.isAllZero && { max: 1 }),
          }),
          ...(['trip-rf-over-time', 'rf-over-time'].includes(id) && {
            min: 0,
            max: 6,
          }),
          ...(["mode-degradation-trip", "undesirable-eb-trip", "unified-kpi-per-project"].includes(id) && {
            min: 0,
          }),
          ticks: {
            stepSize: 1,
            precision: 0,
            ...(['trip-rf-over-time', 'rf-over-time'].includes(id) && {
              autoSkip: false,
            }),
            color: textColor,
            font: {
              size: fontSize,
              family: fontFamily
            }
          },
          border: {
            display: true,
            color: gridColor
          },
          grid: {
            color: gridColor
          },
          ...(yText && {
            title: {
              display: true,
              text: yText,
              color: textColor,
              font: {
                size: fontSize,
                family: fontFamily
              }
            }
          })
        },
        // ...(id === "speed-vs-time" && {
        //   y1: {
        //     position: "right",
        //     grid: {
        //       color: gridColor,
        //       drawOnChartArea: false
        //     },
        //     title: {
        //       display: true,
        //       color: textColor,
        //       text: "Mode"
        //     },
        //     ticks: {
        //       color: textColor,
        //       callback: function (value: number) {
        //         const map: Record<number, string> = {
        //           200: "FS",
        //           160: "OS",
        //           120: "LS",
        //           80: "SR",
        //           40: "SB"
        //         };
        //         return map[value] || "";
        //       }
        //     },
        //     min: 0,
        //     max: 200
        //   }
        // })
      },
      ...(['trip-rf-over-time', 'trip-speed-over-time'].includes(id) && {
        onClick: (event: any, _elements: any, chart: any) => {
          const xScale = chart.scales.x;

          const clickX = event.x;

          const timestamps =
            chart.data.originalTimestamps;

          if (!timestamps?.length) return;

          let leftIndex = -1;
          let rightIndex = -1;

          for (let i = 0; i < timestamps.length - 1; i++) {
            const x1 = xScale.getPixelForValue(i);
            const x2 = xScale.getPixelForValue(i + 1);

            if (clickX >= x1 && clickX <= x2) {
              leftIndex = i;
              rightIndex = i + 1;
              break;
            }
          }

          if (leftIndex === -1) return;

          onRangeSelect?.(
            timestamps[leftIndex],
            timestamps[rightIndex]
          );
        }

      })
    }), [fontSize, theme, xText, yText, id, data, xOffset, yOffset, onRangeSelect, fontFamily]);

  return (
    <div
      className={isScroll ? "overflow-x-auto" : ""}
    >
      <div
        style={{
          minWidth: isScroll
            ? `${(data?.labels?.length || 10) * 80}px`
            : "100%"
        }}
      >
        <Chart
          id={id}
          type="line"
          data={data}
          options={options}
          className={`w-full ${className ?? "h-64"}`}
        />
      </div>
    </div>
  );
}
